import { useState, useEffect, useMemo } from 'react';
import { Rocket, RefreshCw, X, Mail, Phone, Building2, Briefcase, Globe, ExternalLink, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { toast } from 'sonner';
import * as pilotApi from '../services/pilotApplicationsApi';
import type {
  PilotApplication,
  PilotApplicationStatus,
  StatusCounts,
} from '../services/pilotApplicationsApi';

const STATUS_OPTIONS: { value: PilotApplicationStatus; label: string; color: string }[] = [
  { value: 'pending', label: 'En attente', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  { value: 'accepted', label: 'Accepté', color: 'bg-green-100 text-green-800 border-green-200' },
  { value: 'waitlist', label: 'Liste d\'attente', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  { value: 'rejected', label: 'Refusé', color: 'bg-gray-100 text-gray-800 border-gray-200' },
];

const INDUSTRY_LABELS: Record<string, string> = {
  industry: 'Industrie',
  services: 'Services',
  it: 'IT / Tech',
  health: 'Santé',
  logistics: 'Logistique',
  other: 'Autre',
};

const TEAM_SIZE_LABELS: Record<string, string> = {
  '5-15': '5 à 15',
  '16-50': '16 à 50',
  '50+': '50+',
};

type StatusTab = 'all' | PilotApplicationStatus;

type SortKey = 'full_name' | 'company' | 'industry' | 'team_size' | 'status' | 'created_at';
type SortDir = 'asc' | 'desc';

// Ordre logique pour le tri du statut (workflow naturel : pending -> waitlist -> accepted -> rejected)
const STATUS_ORDER: Record<PilotApplicationStatus, number> = {
  pending: 0,
  waitlist: 1,
  accepted: 2,
  rejected: 3,
};

// Ordre numerique pour la taille d'equipe
const TEAM_SIZE_ORDER: Record<string, number> = {
  '5-15': 0,
  '16-50': 1,
  '50+': 2,
};

function statusBadge(status: PilotApplicationStatus): { label: string; color: string } {
  return STATUS_OPTIONS.find((o) => o.value === status) ?? { label: status, color: 'bg-gray-100 text-gray-800 border-gray-200' };
}

function formatDate(iso: string, locale = 'fr-FR'): string {
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

export default function PilotApplicationsPage() {
  const [applications, setApplications] = useState<PilotApplication[]>([]);
  const [counts, setCounts] = useState<StatusCounts | null>(null);
  const [selected, setSelected] = useState<PilotApplication | null>(null);
  const [tab, setTab] = useState<StatusTab>('all');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('created_at');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  const loadAll = async () => {
    setIsLoading(true);
    try {
      const [list, c] = await Promise.all([
        pilotApi.listApplications(tab === 'all' ? undefined : tab),
        pilotApi.getCounts(),
      ]);
      setApplications(list.applications);
      setCounts(c);
    } catch (e) {
      console.error(e);
      toast.error('Impossible de charger les candidatures');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    const base = s
      ? applications.filter(
          (a) =>
            a.full_name.toLowerCase().includes(s) ||
            a.email.toLowerCase().includes(s) ||
            a.company.toLowerCase().includes(s) ||
            a.role.toLowerCase().includes(s),
        )
      : applications;

    const dir = sortDir === 'asc' ? 1 : -1;
    return [...base].sort((x, y) => {
      let cmp = 0;
      switch (sortKey) {
        case 'full_name':
          cmp = x.full_name.localeCompare(y.full_name);
          break;
        case 'company':
          cmp = x.company.localeCompare(y.company);
          break;
        case 'industry':
          cmp = (INDUSTRY_LABELS[x.industry] ?? x.industry).localeCompare(
            INDUSTRY_LABELS[y.industry] ?? y.industry,
          );
          break;
        case 'team_size':
          cmp = (TEAM_SIZE_ORDER[x.team_size] ?? 99) - (TEAM_SIZE_ORDER[y.team_size] ?? 99);
          break;
        case 'status':
          cmp = STATUS_ORDER[x.status] - STATUS_ORDER[y.status];
          break;
        case 'created_at':
          cmp = new Date(x.created_at).getTime() - new Date(y.created_at).getTime();
          break;
      }
      return cmp * dir;
    });
  }, [applications, search, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      // Reception et statut : desc par defaut (plus recent / plus prioritaire en haut)
      setSortDir(key === 'created_at' || key === 'status' ? 'desc' : 'asc');
    }
  };

  const handleStatusChange = async (status: PilotApplicationStatus) => {
    if (!selected) return;
    setIsUpdating(true);
    try {
      const updated = await pilotApi.updateStatus(selected.id, status);
      setSelected(updated);
      setApplications((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
      const c = await pilotApi.getCounts();
      setCounts(c);
      toast.success(`Statut mis à jour : ${statusBadge(status).label}`);
    } catch (e) {
      console.error(e);
      toast.error('Échec de la mise à jour du statut');
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Rocket className="w-6 h-6 text-orange-500" />
            Programme pilote — candidatures
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {counts ? `${counts.total} candidatures au total` : '—'}
          </p>
        </div>
        <button
          onClick={loadAll}
          className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          Rafraîchir
        </button>
      </div>

      {/* Stats */}
      {counts && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard label="Total" value={counts.total} active={tab === 'all'} onClick={() => setTab('all')} />
          <StatCard label="En attente" value={counts.pending} color="text-yellow-700" active={tab === 'pending'} onClick={() => setTab('pending')} />
          <StatCard label="Acceptées" value={counts.accepted} color="text-green-700" active={tab === 'accepted'} onClick={() => setTab('accepted')} />
          <StatCard label="Liste d'attente" value={counts.waitlist} color="text-blue-700" active={tab === 'waitlist'} onClick={() => setTab('waitlist')} />
          <StatCard label="Refusées" value={counts.rejected} color="text-gray-600" active={tab === 'rejected'} onClick={() => setTab('rejected')} />
        </div>
      )}

      {/* Search */}
      <div className="flex items-center gap-3">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher par nom, email, entreprise, rôle…"
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
        <span className="text-sm text-gray-500 whitespace-nowrap">{filtered.length} résultat(s)</span>
      </div>

      {/* List */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Chargement…</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Aucune candidature</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <SortableTh sortKey="full_name" currentKey={sortKey} dir={sortDir} onSort={toggleSort}>Nom</SortableTh>
                <SortableTh sortKey="company" currentKey={sortKey} dir={sortDir} onSort={toggleSort}>Entreprise</SortableTh>
                <SortableTh sortKey="industry" currentKey={sortKey} dir={sortDir} onSort={toggleSort}>Secteur</SortableTh>
                <SortableTh sortKey="team_size" currentKey={sortKey} dir={sortDir} onSort={toggleSort}>Équipe</SortableTh>
                <SortableTh sortKey="status" currentKey={sortKey} dir={sortDir} onSort={toggleSort}>Statut</SortableTh>
                <SortableTh sortKey="created_at" currentKey={sortKey} dir={sortDir} onSort={toggleSort}>Reçue</SortableTh>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((a) => {
                const sb = statusBadge(a.status);
                return (
                  <tr
                    key={a.id}
                    onClick={() => setSelected(a)}
                    className={`cursor-pointer hover:bg-gray-50 transition-colors ${
                      selected?.id === a.id ? 'bg-indigo-50' : ''
                    }`}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{a.full_name}</div>
                      <div className="text-xs text-gray-500">{a.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-gray-900">{a.company}</div>
                      <div className="text-xs text-gray-500">{a.role}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{INDUSTRY_LABELS[a.industry] ?? a.industry}</td>
                    <td className="px-4 py-3 text-gray-700">{TEAM_SIZE_LABELS[a.team_size] ?? a.team_size}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${sb.color}`}>
                        {sb.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{formatDate(a.created_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Detail drawer */}
      {selected && (
        <DetailDrawer
          application={selected}
          isUpdating={isUpdating}
          onClose={() => setSelected(null)}
          onStatusChange={handleStatusChange}
        />
      )}
    </div>
  );
}

interface StatCardProps {
  label: string;
  value: number;
  color?: string;
  active?: boolean;
  onClick: () => void;
}

function StatCard({ label, value, color = 'text-gray-900', active, onClick }: StatCardProps) {
  return (
    <button
      onClick={onClick}
      className={`text-left bg-white rounded-lg border p-4 transition-all ${
        active ? 'border-indigo-500 ring-2 ring-indigo-200' : 'border-gray-200 hover:border-gray-300'
      }`}
    >
      <div className="text-xs text-gray-500 uppercase tracking-wider">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${color}`}>{value}</div>
    </button>
  );
}

interface DetailDrawerProps {
  application: PilotApplication;
  isUpdating: boolean;
  onClose: () => void;
  onStatusChange: (status: PilotApplicationStatus) => void;
}

function DetailDrawer({ application: a, isUpdating, onClose, onStatusChange }: DetailDrawerProps) {
  const sb = statusBadge(a.status);
  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30" />
      <div
        className="relative w-full max-w-xl bg-white shadow-2xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{a.full_name}</h2>
            <p className="text-xs text-gray-500">
              Candidature reçue le {formatDate(a.created_at)}
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Current status + actions */}
          <div>
            <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">Statut actuel</p>
            <div className="flex items-center gap-2 mb-3">
              <span className={`inline-flex items-center px-2.5 py-1 rounded text-sm font-medium border ${sb.color}`}>
                {sb.label}
              </span>
              <span className="text-xs text-gray-500">
                Mise à jour : {formatDate(a.updated_at)}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {STATUS_OPTIONS.map((opt) => {
                const isCurrent = opt.value === a.status;
                return (
                  <button
                    key={opt.value}
                    onClick={() => !isCurrent && onStatusChange(opt.value)}
                    disabled={isCurrent || isUpdating}
                    className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                      isCurrent
                        ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-default'
                        : 'border-gray-300 hover:border-indigo-400 hover:bg-indigo-50 text-gray-700'
                    } disabled:opacity-60`}
                  >
                    {isCurrent ? `✓ ${opt.label}` : opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Contact */}
          <div>
            <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">Contact</p>
            <div className="space-y-2 text-sm">
              <InfoRow icon={Mail} label="Email">
                <a href={`mailto:${a.email}`} className="text-indigo-600 hover:underline inline-flex items-center gap-1">
                  {a.email}
                  <ExternalLink className="w-3 h-3" />
                </a>
              </InfoRow>
              <InfoRow icon={Phone} label="Téléphone">
                <a href={`tel:${a.phone}`} className="text-gray-900">{a.phone}</a>
              </InfoRow>
              <InfoRow icon={Globe} label="Langue">
                {a.locale}
              </InfoRow>
            </div>
          </div>

          {/* Company */}
          <div>
            <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">Entreprise</p>
            <div className="space-y-2 text-sm">
              <InfoRow icon={Building2} label="Société">
                {a.company}
              </InfoRow>
              <InfoRow icon={Briefcase} label="Rôle">
                {a.role}
              </InfoRow>
              <InfoRow icon={Building2} label="Secteur">
                {INDUSTRY_LABELS[a.industry] ?? a.industry}
              </InfoRow>
              <InfoRow icon={Briefcase} label="Taille d'équipe">
                {TEAM_SIZE_LABELS[a.team_size] ?? a.team_size}
              </InfoRow>
            </div>
          </div>

          {/* Motivation */}
          <div>
            <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">Motivation</p>
            <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
              {a.motivation}
            </div>
          </div>

          {/* Audit */}
          {(a.ip_address || a.user_agent) && (
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">Audit</p>
              <div className="text-xs text-gray-500 space-y-1 font-mono">
                {a.ip_address && <div>IP : {a.ip_address}</div>}
                {a.user_agent && <div className="break-all">UA : {a.user_agent}</div>}
                <div>ID : {a.id}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface InfoRowProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  children: React.ReactNode;
}

function InfoRow({ icon: Icon, label, children }: InfoRowProps) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-xs text-gray-500">{label}</div>
        <div className="text-gray-900 break-words">{children}</div>
      </div>
    </div>
  );
}

interface SortableThProps {
  sortKey: SortKey;
  currentKey: SortKey;
  dir: SortDir;
  onSort: (key: SortKey) => void;
  children: React.ReactNode;
}

function SortableTh({ sortKey, currentKey, dir, onSort, children }: SortableThProps) {
  const isActive = sortKey === currentKey;
  const Icon = !isActive ? ChevronsUpDown : dir === 'asc' ? ChevronUp : ChevronDown;
  return (
    <th className="text-left px-4 py-3">
      <button
        onClick={() => onSort(sortKey)}
        className={`inline-flex items-center gap-1 uppercase tracking-wider transition-colors ${
          isActive ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700'
        }`}
      >
        {children}
        <Icon className={`w-3.5 h-3.5 ${isActive ? 'text-indigo-500' : 'text-gray-400'}`} />
      </button>
    </th>
  );
}
